import { useTranslation } from 'react-i18next'
import type { FormState } from './types'

const TIMEZONES = Intl.supportedValuesOf('timeZone')

interface GeneralSettingsProps {
	form: FormState
	updateForm: (updater: (prev: FormState) => FormState) => void
}

export function GeneralSettings({ form, updateForm }: GeneralSettingsProps) {
	const { t } = useTranslation('common')

	return (
		<section className="flex flex-col gap-4">
			<h2 className="text-sm font-medium text-grey-700 uppercase tracking-wide">
				{t('account.availability.general.title')}
			</h2>
			<div className="grid grid-cols-2 gap-4">
				<label className="flex flex-col gap-1">
					<span className="text-sm text-grey-700">
						{t('account.availability.general.buffer_minutes_label')}
					</span>
					<div className="flex items-center gap-2">
						<input
							type="text"
							inputMode="numeric"
							pattern="\d+"
							value={form.bufferMinutes}
							onChange={(e) =>
								updateForm((prev) => ({
									...prev,
									buffer_minutes: Math.max(0, parseInt(e.target.value) || 0),
								}))}
							className="w-24 rounded border border-grey-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
						/>
						<span className="text-sm text-grey-500">
							{t('account.availability.general.buffer_minutes_unit')}
						</span>
					</div>
				</label>
				<label className="flex flex-col gap-1">
					<span className="text-sm text-grey-700">
						{t('account.availability.general.max_bookings_label')}
					</span>
					<input
						type="text"
						inputMode="numeric"
						pattern="\d+"
						value={form.maxBookingsPerDay}
						onChange={(e) =>
							updateForm((prev) => ({
								...prev,
								max_bookings_per_day: Math.max(1, parseInt(e.target.value) || 1),
							}))}
						className="w-24 rounded border border-grey-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
					/>
				</label>
				<label className="flex flex-col gap-1">
					<span className="text-sm text-grey-700">
						{t('account.availability.general.min_advance_notice_label')}
					</span>
					<div className="flex items-center gap-2">
						<input
							type="text"
							inputMode="numeric"
							pattern="\d+"
							value={form.minAdvanceNoticeHours ?? ''}
							onChange={(e) =>
								updateForm((prev) => ({
									...prev,
									min_advance_notice_hours: e.target.value
										? Math.max(0, parseInt(e.target.value))
										: null,
								}))}
							placeholder="24"
							className="w-24 rounded border border-grey-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
						/>
						<span className="text-sm text-grey-500">
							{t('account.availability.general.min_advance_notice_unit')}
						</span>
					</div>
				</label>
			</div>
			<label className="flex flex-col gap-1">
				<span className="text-sm text-grey-700">
					{t('account.availability.general.timezone_label')}
				</span>
				<select
					value={form.timezone}
					onChange={(e) => updateForm((prev) => ({ ...prev, timezone: e.target.value }))}
					className="w-72 rounded border border-grey-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
				>
					{TIMEZONES.map((tz) => (
						<option key={tz} value={tz}>
							{tz}
						</option>
					))}
				</select>
			</label>
		</section>
	)
}
